import { CommonDialogs } from "@mcleod/common";
import { ChangeEvent, DataSourceMode, DataSourceModeChangeEvent, LookupModelSelectionEvent, TableRowDisplayEvent, TableRowMode, Textbox } from "@mcleod/components";
import { Api, LogManager } from "@mcleod/core";
import { DuplicateId, DuplicateIdProps } from "@mcleod/dispatch";
import { AutogenLayoutMobileCommUnit } from "./autogen/AutogenLayoutMobileCommUnit";

const log = LogManager.getLogger("lme.dispatch.mobilecomm.MobileCommUnit");


const viewMCUnitCodeURL = "lme/dispatch/mobilecomm/MobileCommUnit?mode=update&key=";
const DUPLICATE_VALIDATION_PROPS: DuplicateIdProps = {
    tableName: "mc_unit",
    screenName: "Mobile Comm Unit",
    navigateToUrl: viewMCUnitCodeURL
};

export class MobileCommUnit extends AutogenLayoutMobileCommUnit {
    private origReeferId: string;

    private static readonly UNIT_TYPE_QUICK_INFO_LAYOUTS: { [key: string]: string } = {
        "D": "lme/dispatch/asset/DriverQuickInfo",
        "Driver": "lme/dispatch/asset/DriverQuickInfo",
        "T": "lme/dispatch/asset/TractorQuickInfo",
        "Tractor": "lme/dispatch/asset/TractorQuickInfo",
        "L": "lme/dispatch/asset/TrailerQuickInfo",
        "Trailer": "lme/dispatch/asset/TrailerQuickInfo"
    };

    async onLoad() {
        DuplicateId.configureIdTextbox(this.textboxId, DUPLICATE_VALIDATION_PROPS);
        this.addLayoutLoadListener(event => {
            this.textboxUnitType.allowTooltip = false;
            this.updateUnitNumberQuickInfo();
        });

        this.textboxReeferId.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            this.reeferIdModelSelection(event);
        });
    }

    /**
     * Sets the quick info layout shown when hovering the Unit number based on the
     * selected unit type (D = Driver, T = Tractor, L = Trailer).
     */
    private updateUnitNumberQuickInfo(unitType?: string) {
        const type = unitType ?? this.mainDataSource.activeRow?.get("mc_unit_type");
        this.textboxId.quickInfoLayout = MobileCommUnit.UNIT_TYPE_QUICK_INFO_LAYOUTS[type] ?? null;
    }

    tableMCUnitDetailOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        if (tableRow.mode != TableRowMode.QUICK_ADD) {
            const id = tableRow.data.get("application_id")
            const desc = tableRow.data.get("application_desc")
            const textboxApplicationDesc = tableRow.findComponentById("textboxApplicationDesc") as Textbox;

            if (desc == undefined) {
                // Use ID to find correct description
                textboxApplicationDesc.setSelectedItemFromValue(id);
            } else {
                textboxApplicationDesc.text = desc;
            }
        }
    }

    sourceMCUnitAllAfterModeChange(event: DataSourceModeChangeEvent) {
        if ("search" === this.mainDataSource.mode) {
            this.setDetailTableVisible(true);
        } else if ("update" === this.mainDataSource.mode) {
            this.setDetailTableVisible(this.activeRow?.get("mc_control_id") === "QCPORTAL");
            this.textboxReeferId.visible = this.activeRow?.get("mc_unit_type") === "L" && this.activeRow?.get("has_reefer_tracking") === true;
        }
        this.updateUnitNumberQuickInfo();
    }

    onLookupModelSelectionMCControl(event: LookupModelSelectionEvent) {
        const data = this.textboxVendor.lookupModelData;
        const selectedRow = event.selection;

        if (!data || data.length === 0) {
            this.labelDetail.visible = false;
            this.tableMCUnitDetail.visible = false;
            return;
        }

        if (selectedRow) {
            const controlID = selectedRow.get("id", null);
            this.setDetailTableVisible(controlID === "QCPORTAL");
        }

    }

    textboxVendorBeforeLookupModelSearch(event) {
        event.filter.is_active = 'Y';
    }

    private setDetailTableVisible(visible: boolean) {
        this.labelDetail.visible = visible;
        this.tableMCUnitDetail.visible = visible;
    }

    textboxUnitTypeOnChange(event: ChangeEvent){
        this.textboxReeferId.visible = event.newValue === "Trailer";
        this.updateUnitNumberQuickInfo(event.newValue as string);

        if (event.oldValue === "Trailer") {
            this.origReeferId = this.mainDataSource.activeRow.get("reefer_id");
        }

        if (event.newValue === "Trailer") {
            this.mainDataSource.activeRow.set("reefer_id", this.origReeferId);
        } else {
            this.mainDataSource.activeRow.set("reefer_id", null);
        }

        this.textboxReeferId.displayData(this.mainDataSource.activeRow, null, 0);
    }

    private async reeferIdModelSelection(event: LookupModelSelectionEvent) {
        if (this.mainDataSource.mode != DataSourceMode.SEARCH && event.hasSelection() === true) {
            const response = await Api.search("lme/dispatch/mobilecomm/get-update-trailer-reefer", {
                "reefer": event.selection?.get("id"),
                "excluded_trailer": this.mainDataSource.activeRow?.get("id")
            });

            if (response.data?.[0]?.trailer_list === undefined) {
                this.mainDataSource.activeRow.set("sync_trailers", false);
                return;
            }

            const userConfirmed = await this.confirmChangeTrailer(response.data[0].trailer_list);
            this.mainDataSource.activeRow.set("sync_trailers", userConfirmed);
            if (!userConfirmed) {
                const origRow = this.mainDataSource.activeRow.originalData;
                this.textboxReeferId.text = origRow["trailer.reefer_id"];
                this.textboxReeferId.focus();
            }
        }
    }

    private async confirmChangeTrailer(defaultTrailerList: any): Promise<boolean> {
        return await CommonDialogs.showYesNo(
            `Only one reefer can be associated with a trailer, selecting "Yes" will remove the selected reefer from trailer(s): ${this.getTrailerList(defaultTrailerList)}`,
            "Change Trailer Reefer"
        );
    }

    private getTrailerList(default_trailer_list: any): string {
        let result = "\n\n";
        for (const trailer of default_trailer_list) {
            result += trailer.trailer_id + "\n";
        }
        return result;
    }
}
